import { useCallback, useEffect, useRef, useState } from 'react';
import { humanErrorMessage } from '~/lib/api-client';

export type VoiceSttState = 'idle' | 'recording' | 'transcribing' | 'error';

interface ListenResponse {
  transcript: string;
  model: string;
}

interface UseVoiceSttOptions {
  onTranscript: (text: string) => void;
  /** Optional LiteLLM model id (e.g. `whisper-1`, `azure/whisper`). */
  model?: string;
}

function pickMimeType(): string {
  // Order from most-supported in modern Chromium/Firefox/Safari first.
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
  ];
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) {
      return m;
    }
  }
  return '';
}

export function useVoiceStt({ onTranscript, model }: UseVoiceSttOptions) {
  const [state, setState] = useState<VoiceSttState>('idle');
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const mimeRef = useRef<string>('');
  // FRONT-2431 fix round 1: `stopAndTranscribe` awaits a fetch, so the owning
  // component can unmount while it's in flight — e.g. the phone composer
  // remounts per chat when the person switches chats from the Chats sheet
  // mid-dictation. Without this, `onTranscript` and the state setters below
  // still fire on the unmounted instance: React drops the state update, but
  // `onTranscript` itself has no such guard and would hand the words to
  // whatever component now owns that callback identity — i.e. the next
  // chat's draft.
  const mountedRef = useRef(false);

  const cleanup = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const start = useCallback(async () => {
    if (state === 'recording' || state === 'transcribing') return;
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMimeType();
      mimeRef.current = mime;
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onerror = (e) => {
        console.error('[voice-stt] recorder error', e);
        setError('Recording failed');
        setState('error');
        cleanup();
      };
      recorder.start();
      recorderRef.current = recorder;
      setState('recording');
    } catch (err) {
      console.error('[voice-stt] mic access failed', err);
      setError((err as Error).message || 'Microphone access denied');
      setState('error');
      cleanup();
    }
  }, [state, cleanup]);

  const stopAndTranscribe = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder || state !== 'recording') return;

    setState('transcribing');

    const stopped = new Promise<void>((resolve) => {
      recorder.addEventListener('stop', () => resolve(), { once: true });
    });
    recorder.stop();
    await stopped;
    // The component may have unmounted while we were waiting on the recorder
    // to actually stop (see the docblock on `mountedRef` above) — nothing
    // left to update, and no listener left to hand a transcript to.
    if (!mountedRef.current) return;

    const mime = mimeRef.current || 'audio/webm';
    const blob = new Blob(chunksRef.current, { type: mime });
    cleanup();

    if (blob.size === 0) {
      setState('idle');
      return;
    }

    try {
      const url = model
        ? `/api/voice/listen?model=${encodeURIComponent(model)}`
        : '/api/voice/listen';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': mime },
        body: blob,
      });
      if (!mountedRef.current) return;
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        // FRONT-1544: as in speak-button — a truncated HTML page is still
        // markup in front of a human.
        throw new Error(humanErrorMessage(res.status, detail));
      }
      const data = (await res.json()) as ListenResponse;
      if (!mountedRef.current) return;
      if (data.transcript) onTranscript(data.transcript);
      setState('idle');
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('[voice-stt] transcribe failed', err);
      setError((err as Error).message);
      setState('error');
    }
  }, [state, cleanup, onTranscript, model]);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        /* ignore */
      }
    }
    cleanup();
    setState('idle');
  }, [cleanup]);

  return { state, error, start, stopAndTranscribe, cancel };
}
